/**
 * Iter 187 — Universal "share file directly" helper.
 *
 * Dirk's complaint: long-pressing / right-clicking a photo in the app and
 * picking "Share via WhatsApp" was passing a URL instead of the bytes —
 * recipients hit a broken download page on the other end.
 *
 * Contract:
 *   - Try `navigator.share({ files: [File] })` first. Native OS share sheet
 *     on iOS Safari, Android Chrome, Edge mobile — picks WhatsApp / Email /
 *     Drive / etc. and ATTACHES the real file.
 *   - If the platform can't share files (desktop Chrome on most Linux,
 *     Firefox, older browsers), fall back to a silent download and toast
 *     the user with "File downloaded — drop it into WhatsApp/Email/etc.".
 *
 * The helper accepts ANY shape of media descriptor we have across the app:
 *   { url, filename, contentType, title? }
 *   { upload_id, filename?, content_type? }
 *   { file_url, file_name?, content_type? }
 *   { src }                                   ← bare image src
 */
import { toast } from "sonner";
import { BACKEND_URL } from "./api";

const _filenameFromUrl = (url) => {
  if (!url) return "download";
  try {
    const u = new URL(url, window.location.href);
    const last = (u.pathname.split("/").pop() || "").split("?")[0];
    return last || "download";
  } catch {
    const parts = url.split("?")[0].split("/");
    return parts[parts.length - 1] || "download";
  }
};

const _extFromMime = (mime) => {
  if (!mime) return "";
  const map = {
    "image/jpeg": ".jpg", "image/jpg": ".jpg", "image/png": ".png",
    "image/webp": ".webp", "image/gif": ".gif", "image/heic": ".heic",
    "image/heif": ".heif",
    "video/mp4": ".mp4", "video/quicktime": ".mov", "video/webm": ".webm",
    "audio/mpeg": ".mp3", "audio/mp3": ".mp3", "audio/wav": ".wav",
    "audio/webm": ".webm", "audio/ogg": ".ogg", "audio/mp4": ".m4a",
    "application/pdf": ".pdf",
    "text/plain": ".txt", "text/csv": ".csv",
  };
  return map[mime.split(";")[0].toLowerCase()] || "";
};

const _ensureExt = (name, mime) => {
  if (!name) return `share${_extFromMime(mime) || ""}`;
  if (name.includes(".")) return name;
  return name + _extFromMime(mime);
};

const _dataUrlToBlob = (dataUrl) => {
  const [head, body] = dataUrl.split(",", 2);
  const mime = (head.match(/data:([^;]+)/) || [null, "application/octet-stream"])[1];
  const isB64 = /;base64/i.test(head);
  if (isB64) {
    const bin = atob(body);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
  }
  return new Blob([decodeURIComponent(body)], { type: mime });
};

/**
 * Normalise any of our media descriptors into { url, filename, contentType, title }.
 * Returns null if we can't derive a fetchable URL.
 */
export function resolveShareDescriptor(input = {}) {
  if (!input) return null;
  let url = input.url || input.src || input.file_url || input.upload_url || null;
  const filename = input.filename || input.file_name || input.upload_filename || null;
  const contentType = input.contentType || input.content_type || input.upload_content_type || null;
  const title = input.title || input.label || filename || null;

  // upload_id only? Construct the /api/uploads URL.
  if (!url && input.upload_id) {
    const token = (typeof localStorage !== "undefined" && localStorage.getItem("hv_token")) || "";
    url = `${BACKEND_URL}/api/uploads/${input.upload_id}?auth=${encodeURIComponent(token)}`;
  }

  // Sometimes the FE has a bare upload_id string in a photos[] array.
  if (!url && typeof input === "string") {
    const token = (typeof localStorage !== "undefined" && localStorage.getItem("hv_token")) || "";
    if (input.startsWith("data:") || input.startsWith("http")) {
      url = input;
    } else {
      url = `${BACKEND_URL}/api/uploads/${input}?auth=${encodeURIComponent(token)}`;
    }
  }

  if (!url) return null;
  return { url, filename, contentType, title };
}

const _fetchAsBlob = async (url) => {
  if (url.startsWith("data:")) return _dataUrlToBlob(url);
  // credentials:'omit' is intentional — our /api/uploads URLs carry the
  // auth token in the `?auth=` querystring (see uploadUrl in lib/uploads.js).
  // Flipping this to 'include' triggers a strict CORS preflight that
  // iOS Safari + R2 signed URLs both reject. Don't change this.
  const resp = await fetch(url, { credentials: "omit" });
  if (!resp.ok) throw new Error(`Fetch failed (HTTP ${resp.status})`);
  return resp.blob();
};

const _downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
};

/**
 * The one universal share entry point. Returns:
 *   { method: "share" | "download" | "cancelled" | "error", error?: Error }
 *
 * Always shows a toast on completion / error so callers don't have to.
 */
export async function shareFile(input) {
  const desc = resolveShareDescriptor(input);
  if (!desc) {
    toast.error("Nothing to share.");
    return { method: "error", error: new Error("no descriptor") };
  }
  let toastId;
  try {
    toastId = toast.loading("Preparing file…");
    const blob = await _fetchAsBlob(desc.url);
    const filename = _ensureExt(desc.filename || _filenameFromUrl(desc.url), blob.type || desc.contentType);
    const file = new File([blob], filename, { type: blob.type || desc.contentType || "application/octet-stream" });

    // Web Share API (file mode) — works on iOS Safari + Android Chrome.
    if (
      typeof navigator !== "undefined"
      && typeof navigator.canShare === "function"
      && navigator.canShare({ files: [file] })
      && typeof navigator.share === "function"
    ) {
      toast.dismiss(toastId);
      try {
        await navigator.share({
          files: [file],
          title: desc.title || filename,
          text: desc.title || filename,
        });
        return { method: "share" };
      } catch (err) {
        // User cancelled → silent
        if (err?.name === "AbortError") return { method: "cancelled" };
        // Some browsers throw NotAllowedError when share is triggered
        // outside a user gesture chain (e.g. after our async fetch).
        // Fall through to download.
        // eslint-disable-next-line no-console
        console.warn("[shareFile] navigator.share failed, falling back to download:", err);
      }
    } else {
      toast.dismiss(toastId);
    }

    // Fallback — auto-download + toast with instructions.
    _downloadBlob(blob, filename);
    toast.success("File downloaded", {
      description: "Drop it into WhatsApp, Email, Drive — whatever you like.",
      duration: 5000,
    });
    return { method: "download" };
  } catch (err) {
    if (toastId) toast.dismiss(toastId);
    toast.error("Share failed", {
      description: err?.message?.slice(0, 120) || "Try again in a moment.",
    });
    return { method: "error", error: err };
  }
}

/**
 * Quick check used by UI to decide whether to render a share button at all
 * (we currently render it unconditionally — fallback works everywhere).
 */
export function isFileShareSupported() {
  return (
    typeof navigator !== "undefined"
    && typeof navigator.canShare === "function"
    && typeof navigator.share === "function"
  );
}
